import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { useAppStore } from '../../app/hooks/useAppStore';
import { MODULES, getFamiliesForModule, getAllowedTiersForGroup, getModuleIcon, getModuleColors } from '../../content';
import { getFamilyMasteryKey } from '../../mastery';
import { getMasteryLabelInfo } from '../shared/masteryLabelInfo';
import { CardSurface } from '../shared/CardSurface';
import { CenteredModal } from '../shared/CenteredModal';
import { MODE_THEME } from '../shared/modeTheme';
interface FluencyModulePickerProps {
  isOpen: boolean;
  onSelectAll: () => void;
  onSelectModule: (moduleId: string) => void;
  onCancel: () => void;
}
export function FluencyModulePicker({
  isOpen,
  onSelectAll,
  onSelectModule,
  onCancel
}: FluencyModulePickerProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  const fluencyTheme = MODE_THEME['fluency'];
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  const moduleAggregates = useMemo(() => {
    return MODULES.map(module => {
      const families = getFamiliesForModule(module.id);
      const moduleFamilies = families.filter(family => {
        if (!allowedTiers.has(family.tier)) return false;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey];
        return mastery && mastery.attempts > 0;
      });
      const count = moduleFamilies.length;
      const avgMastery = count > 0 ? moduleFamilies.reduce((sum, f) => {
        const masteryKey = getFamilyMasteryKey(f.familyId, selectedGroup);
        return sum + (familyMastery[masteryKey]?.masteryScore ?? 0);
      }, 0) / count : 0;
      return {
        module,
        count,
        avgMastery
      };
    }).filter(m => m.count > 0);
  }, [selectedGroup, familyMastery, allowedTiers]);
  return <CenteredModal isOpen={isOpen} onClose={onCancel} maxWidth="3xl" panelClassName="bg-slate-50 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-2912">
      {/* Header */}
      <div className={`bg-gradient-to-r ${fluencyTheme.gradient} p-6 text-white text-center relative flex-shrink-0`} data-id="element-2913">
        <button onClick={onCancel} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors" data-id="element-2914">
          <X className="w-5 h-5" data-id="element-2915" />
        </button>
        <div className="flex justify-center mb-3" data-id="element-2916">
          <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} animate={true} data-id="element-2917" />
        </div>
        <h2 className="text-2xl font-black tracking-tight" data-id="element-2918">
          Wat wil je oefenen?
        </h2>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-2919">
        <Button variant="primary" onClick={onSelectAll} className={`w-full py-4 mb-8 bg-gradient-to-r ${fluencyTheme.gradient} hover:brightness-110 border-none shadow-lg ${fluencyTheme.shadow} text-lg`} data-id="element-2920">
          <Sparkles className="w-5 h-5 mr-2" data-id="element-2921" />
          Alles door elkaar
        </Button>

        {moduleAggregates.length > 0 && <div data-id="element-2922">
            <h3 className="text-lg font-black text-slate-800 mb-4 text-center" data-id="element-2923">
              Of kies een specifiek vak
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-2924">
              {moduleAggregates.map(({
            module,
            count,
            avgMastery
          }, index) => {
            const ModIcon = getModuleIcon(module.icon);
            const colors = getModuleColors(module.color);
            const masteryInfo = getMasteryLabelInfo(avgMastery);
            return <CardSurface key={module.id} onClick={() => onSelectModule(module.id)} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: Math.min(index * 0.05, 0.3)
            }} className="w-full text-left p-4 rounded-3xl border-2 border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg transition-all group relative overflow-hidden flex flex-col h-full" data-id="element-2925">
                    <div className="flex items-start justify-between mb-3" data-id="element-2926">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center ${colors.light}`} data-id="element-2927">
                        <ModIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.text}`} data-id="element-2928" />
                      </div>
                      <div className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-1 rounded-lg" data-id="element-2929">
                        {count} items
                      </div>
                    </div>

                    <h3 className="text-sm sm:text-base font-black text-slate-800 mb-1 group-hover:text-slate-900 transition-colors" data-id="element-2930">
                      {module.title}
                    </h3>

                    <div className="mt-auto pt-3" data-id="element-2931">
                      <div className="flex items-center justify-between mb-1.5" data-id="element-2932">
                        <span className={`text-[10px] sm:text-xs font-bold ${masteryInfo.color}`} data-id="element-2933">
                          {masteryInfo.label}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400" data-id="element-2934">
                          {Math.round(avgMastery)}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2935">
                        <motion.div className="h-full rounded-full" style={{
                    backgroundColor: masteryInfo.ring
                  }} initial={{
                    width: 0
                  }} animate={{
                    width: `${Math.min(100, Math.max(0, avgMastery))}%`
                  }} transition={{
                    duration: 0.8,
                    ease: 'easeOut'
                  }} data-id="element-2936" />
                      </div>
                    </div>
                  </CardSurface>;
          })}
            </div>
          </div>}
      </div>
    </CenteredModal>;
}